import { useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import styles from './Dialog.module.css';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useFocusTrap } from '../hooks/useFocusTrap';

export type DialogWidth = 'default' | 'wide' | 'xl' | 'fullscreen';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: DialogWidth;
  // Footer slot — typically the cancel + primary action button pair.
  footer?: ReactNode;
  // Whether clicking the backdrop dismisses. Off by default — destructive
  // dialogs and forms should not lose state to an errant click.
  dismissOnBackdrop?: boolean;
  children: ReactNode;
}

const WIDTH_CLASS: Record<DialogWidth, string> = {
  default: styles.widthDefault ?? '',
  wide: styles.widthWide ?? '',
  xl: styles.widthXl ?? '',
  fullscreen: styles.widthFullscreen ?? '',
};

export function Dialog({
  open,
  onClose,
  title,
  width = 'default',
  footer,
  dismissOnBackdrop = false,
  children,
}: DialogProps) {
  const titleId = useId();
  useEscapeKey(open, onClose);
  const panelRef = useFocusTrap<HTMLDivElement>(open);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className={styles.backdrop}
        onClick={dismissOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${styles.panel} ${WIDTH_CLASS[width]}`}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={18} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </header>
        <div className={styles.content}>{children}</div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </>,
    document.body,
  );
}
